/**
 * expr —— 受限表达式引擎（可选节点包的**数据求值器**）。
 *
 * ```ts
 * const compiled = compileExpression("({ out: helpers.num(api.input('a')) * 2 })")
 * if (compiled.ok) compiled.evaluator(api)   // → { out: ... }
 * ```
 *
 * 设计取舍、白名单、比较语义见各子模块的头注释。
 * 一句话：**源码不经过 JS 引擎**，所以它既是 CSP 友好的，也是真沙箱。
 */
import type { NodeEvalApi, NodeEvaluator } from '../evaluate'
import type { ExprProblem } from './ast'
import { evaluateExpr } from './evaluate'
import { parseExpression } from './parse'
import { repeaterHandle } from '../rows'

export type { ExprProblem } from './ast'
export { HELPER_DOC, HELPER_NAMES, HELPERS } from './helpers'
export { ExprRuntimeError } from './evaluate'
export { API_METHODS, API_PROPS } from './whitelist'

export type CompiledExpression =
  | {
      ok: true
      evaluator: NodeEvaluator
      /**
       * 求值结果的**形状**：表达式有没有写 `{ out: … }`。
       *
       * 求值器必须返回「端口 id → 值」的对象；只写一个标量是最常见的
       * 写法错误（`helpers.num(a) * 2` 这种）。编译期能看出来，
       * 就在这里说清楚，而不是等运行时报「返回值不是对象」。
       */
      shape: 'object' | 'other'
    }
  | { ok: false; problems: ExprProblem[] }

/**
 * 编译一段表达式源码。
 *
 * ⚠️ 编译**一次**，结果缓存（见 `evaluatorSource.ts`）。这条路径在求值
 * 热循环里，每次重新解析源码会是灾难性的开销。
 */
export function compileExpression(source: string): CompiledExpression {
  const parsed = parseExpression(source)
  if (!parsed.ok) return parsed

  const ast = parsed.ast
  return {
    ok: true,
    shape: ast.kind === 'object' ? 'object' : 'other',
    evaluator: (api: NodeEvalApi) => {
      /*
       * ★ `api.outputs('out', [...])` 的落点。
       *
       * 表达式的返回值是「端口 id → 值」，键在源码里是**写死的**；
       * 而输出变参行的实例数运行时才知道，静态键表达不了 ——
       * 所以这些实例在求值过程中**收集**起来，最后并进返回值。
       *
       * ⚠️ 合并顺序：`outputs()` 收上来的**覆盖**字面量键 ——
       * 作者显式写了 `api.outputs(...)`，那就是他想要的答案。
       */
      const collected: Record<string, unknown> = {}
      const scoped = {
        ...api,
        outputs: (rowId: unknown, values: unknown) => {
          const row = api.model.rows.find((r) => r.id === String(rowId))
          if (!row || row.kind !== 'repeater') {
            throw new Error(`api.outputs：没有名为「${String(rowId)}」的变参行`)
          }
          if ((row.side ?? 'input') !== 'output') {
            throw new Error(
              `api.outputs 只能写**输出方向**的变参行；「${row.id}」是输入方向的` +
                `（输入由上游喂，求值器改不了）`,
            )
          }
          const list = Array.isArray(values) ? values : [values]
          list.forEach((value, index) => {
            collected[repeaterHandle(row, index)] = value
          })
        },
      }
      const value = evaluateExpr(ast, scoped)
      /*
       * 求值器的契约是「返回 端口 id → 值」。有人会直接写一个标量或者
       * 一个数组 —— 那时**不要**硬包成 `{ out: value }`：
       * 猜端口名会让「写错了」变成一个看起来能跑但结果不对的东西。
       * 明确报错，信息里给出正确写法。
       */
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error(
          `表达式的值必须是一个对象（端口 id → 值），比如 \`({ out: … })\`；` +
            `现在算出来的是 ${describe(value)}`,
        )
      }
      return { ...(value as Record<string, unknown>), ...collected }
    },
  }
}

function describe(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return '数组'
  if (value === undefined) return 'undefined'
  return `${typeof value}（${String(value).slice(0, 40)}）`
}
